/**
 * Returns base64 data URL for an external image using XMLHttpRequest.
 * @memberof utils
 */
export async function getBase64FromExternalImage(url) {
    return new Promise(function (resolve) {
        var xhr = new XMLHttpRequest();

        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                var reader = new window.FileReader();
                reader.readAsDataURL(this.response);
                reader.onloadend = () => {
                    let base64data = reader.result;
                    resolve(base64data);
                };
            }
        };

        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.send();
    });
}

/**
 * Bypass the CDN using the bucket and region configured at build time.
 * @param {string|null|undefined} logoUrl - Stored public logo URL.
 * @returns {string|null|undefined} Direct S3 URL for the configured bucket, or the original URL.
 */
export const changeLogoUrlToAWS = (logoUrl) => {
    const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME || 'rpl-robot';
    const region = process.env.REACT_APP_AWS_S3_REGION || 'ap-southeast-2';

    if (typeof logoUrl !== 'string' || !bucketName) return logoUrl;

    const prefix = `https://${bucketName}/`;
    if (!logoUrl.startsWith(prefix)) return logoUrl;

    return `https://s3.${region}.amazonaws.com/${bucketName}/${logoUrl.slice(prefix.length)}`;
};

/**
 * Encodes canvas contents as a PNG file
 *
 * @param {HTMLCanvasElement} canvas - Canvas containing the processed image
 * @param {string} fileName - Name assigned to the PNG file
 * @returns {Promise<File>} PNG image file
 */
export const canvasToPngFile = (canvas, fileName = 'client-logo.png') =>
    new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
            }

            resolve(
                new File([blob], fileName, {
                    type: 'image/png',
                    lastModified: Date.now()
                })
            );
        }, 'image/png');
    });

export const resizeImageFile = (file, targetWidth, targetHeight) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const sourceUrl = URL.createObjectURL(file);

        img.onload = async () => {
            URL.revokeObjectURL(sourceUrl);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Image processing is unavailable in this browser'));
                return;
            }

            canvas.width = targetWidth;
            canvas.height = targetHeight;

            // SCALE TO COVER
            const scale = Math.max(targetWidth / img.width, targetHeight / img.height);

            const drawWidth = img.width * scale;
            const drawHeight = img.height * scale;

            // center the image, overflowing on 1 axis (cropped)
            const offsetX = (targetWidth - drawWidth) / 2;
            const offsetY = (targetHeight - drawHeight) / 2;

            // optional: clear canvas instead of white background
            ctx.clearRect(0, 0, targetWidth, targetHeight);
            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

            try {
                const resizedFile = await canvasToPngFile(canvas);
                resolve({
                    file: resizedFile,
                    url: URL.createObjectURL(resizedFile)
                });
            } catch (error) {
                reject(error);
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(sourceUrl);
            reject(new Error('The selected image is invalid or unsupported'));
        };
        img.src = sourceUrl;
    });
